import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { CarouselApi } from "@/components/ui/carousel";

function getFullyVisibleSlideCount(api: NonNullable<CarouselApi>): number {
  const rootNode = api.rootNode();
  const slideNodes = api.slideNodes();
  if (!rootNode || slideNodes.length === 0) return 1;

  const containerWidth = rootNode.getBoundingClientRect().width;

  // Use the second slide if available (it has the typical gap margin),
  // otherwise fall back to the first slide.
  const measureSlide = slideNodes.length > 1 ? slideNodes[1] : slideNodes[0];
  const slideStyle = window.getComputedStyle(measureSlide);
  const slideWidth =
    measureSlide.getBoundingClientRect().width +
    parseFloat(slideStyle.marginLeft || "0") +
    parseFloat(slideStyle.marginRight || "0");

  if (slideWidth <= 0) return 1;

  return Math.floor(containerWidth / slideWidth);
}

function getSlidesNumberToScrollThrough(
  api: NonNullable<CarouselApi>,
  extraScrollMoveMobile: boolean,
  current: number,
  max: number,
  direction: "next" | "prev",
) {
  if (max <= 1) {
    return 0;
  }

  const fullyVisible = getFullyVisibleSlideCount(api);
  const isAtEdge = current === 0 || current === max - 1;
  const extraOneMove = current === 0 && extraScrollMoveMobile ? 1 : 0;
  const baseScrollAmount = isAtEdge
    ? Math.max(1, fullyVisible) + extraOneMove
    : Math.max(1, fullyVisible - 1);

  if (direction === "next") {
    const remainingSlides = max - 1 - current;
    return Math.max(0, Math.min(baseScrollAmount, remainingSlides));
  }

  if (direction === "prev") {
    const remainingSlides = current;
    return Math.max(0, Math.min(baseScrollAmount, remainingSlides));
  }

  return 1; // Fallback
}

export const SliderWrapper = ({
  children,
  api,
  activeIndex = 0,
  hideOverlay = false,
  extraScrollMoveMobile = false,
  centerArrows = false,
}: {
  children: ReactNode;
  api: CarouselApi | undefined | null;
  activeIndex?: number;
  hideOverlay?: boolean;
  extraScrollMoveMobile?: boolean;
  centerArrows?: boolean;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLElement | null>(null);

  const [current, setCurrent] = useState(activeIndex);
  const [buttonPosition, setButtonPosition] = useState<string>("50%");

  const scrollPrev = () => {
    if (!api) {
      return;
    }
    const visibleCount = getSlidesNumberToScrollThrough(
      api,
      extraScrollMoveMobile,
      current,
      api.slideNodes().length,
      "prev",
    );
    api?.scrollTo(current - visibleCount);
  };

  const scrollNext = () => {
    if (!api) {
      return;
    }
    const visibleCount = getSlidesNumberToScrollThrough(
      api,
      extraScrollMoveMobile,
      current,
      api.slideNodes().length,
      "next",
    );
    api?.scrollTo(current + visibleCount);
  };

  function updateButtonPos() {
    if (centerArrows) return;
    const height = ref.current?.querySelector("img")?.height;
    if (height) {
      setButtonPosition(`${height / 2}px`);
    }
  }

  useEffect(() => {
    updateButtonPos();
  }, []);

  const onSelect = useCallback(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    if (!api) return;

    function handleResize() {
      if (!api) return;
      onSelect();

      api.scrollTo(current);

      api.on("select", onSelect);

      updateButtonPos();
    }

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [api, onSelect]);

  return (
    <div className={"relative"}>
      {!hideOverlay && (
        <>
          <div
            style={{
              // Kolor przejścia jedzie zmienną z fallbackiem na biel, bo maska musi zgadzać się z tłem
              // sekcji - w trybie ciemnym egzaminów biały gradient kładł się smugą na kafelkach.
              // Fallback trzyma dotychczasowe zachowanie wszędzie poza sekcjami, które zmienną ustawiają.
              background: `linear-gradient(to right,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 1) 0%,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 0.975) 10%,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 0.95) 20%,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 0.9) 30%,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 0.8) 40%,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 0.7) 50%,
                  rgb(var(--slider-fade-rgb, 255 255 255) / 0) 100%
                )`,
            }}
            className={cn(
              "absolute top-0 bottom-0 -left-4 z-10 hidden w-16 transition-all sm:block",
              "@512:-left-1",
              "@704:w-[120px]",
              current > 0
                ? "pointer-events-auto opacity-100"
                : "pointer-events-none opacity-0",
            )}
          />
          <div
            style={{
              background: `linear-gradient(to left,
                rgb(var(--slider-fade-rgb, 255 255 255) / 1) 0%,
                rgb(var(--slider-fade-rgb, 255 255 255) / 0.975) 10%,
                rgb(var(--slider-fade-rgb, 255 255 255) / 0.95) 20%,
                rgb(var(--slider-fade-rgb, 255 255 255) / 0.9) 30%,
                rgb(var(--slider-fade-rgb, 255 255 255) / 0.8) 40%,
                rgb(var(--slider-fade-rgb, 255 255 255) / 0.7) 50%,
                rgb(var(--slider-fade-rgb, 255 255 255) / 0) 100%
              )`,
            }}
            className={cn(
              "absolute top-0 -right-4 bottom-0 z-10 hidden w-16 transition-all sm:block",
              "@512:-right-1",
              "@704:w-[120px]",
              current < count - 1
                ? "pointer-events-auto opacity-100"
                : "pointer-events-none opacity-0",
            )}
          />
        </>
      )}

      {current > 0 && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute -left-2 z-20 h-8 w-8 -translate-y-1/2 rounded-full bg-white/80 shadow-md hover:bg-white",
            "@512:left-2",
            current === 0 && "cursor-not-allowed opacity-50",
          )}
          style={{ top: buttonPosition }}
          onClick={scrollPrev}
          disabled={current === 0}
          aria-label="Previous slide"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
      )}
      {current < count - 1 && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute -right-2 z-20 h-8 w-8 -translate-y-1/2 rounded-full bg-white/80 shadow-md hover:bg-white",
            "@512:right-2",
            current === count - 1 && "cursor-not-allowed opacity-50",
          )}
          onClick={scrollNext}
          disabled={current === count - 1}
          aria-label="Next slide"
          style={{ top: buttonPosition }}
        >
          <ArrowRightIcon className="h-4 w-4" />
        </Button>
      )}
      <div ref={ref} className={"-mx-4 sm:mx-0"}>
        {children}
      </div>
    </div>
  );
};
